/* Session universe + time-series derivation. Sessions are generated lazily and
 * deterministically from the resolved query's seed; trends bucketize the
 * window with a weekday/weekend rhythm so charts read like real traffic. */

import {
  buildContextBundle,
  buildCostDecomposition,
  buildLatency,
  biasAverages,
  round2,
  tokensFromCost,
  type ResolvedQuery,
} from "./derive";
import { BASE, CONFIG_IDS, EXPERIMENTS, MODELS, repoWeight } from "./catalog";
import { Rng, hashString, splitByWeights } from "./seed";
import type {
  CostBreakdown,
  SessionDetail,
  SessionRow,
  SortKey,
  SortOrder,
  TrendBucket,
  TrendPoint,
} from "../types";

/** Cap on materialized session rows; `total` still reports the real count. */
const MAX_MATERIALIZED = 400;
const DAY_MS = 86_400_000;
const HOUR_MS = 3_600_000;

export function shortId(seed: number): string {
  // UUID-ish, stable per seed.
  const rng = new Rng(seed);
  const hex = (n: number) =>
    Array.from({ length: n }, () =>
      Math.floor(rng.float() * 16).toString(16),
    ).join("");
  return `${hex(8)}-${hex(4)}-4${hex(3)}-${hex(4)}-${hex(12)}`;
}

/** Session id for the Nth session in a scope. The single source of truth for
 *  session identity so links from other mock surfaces (e.g. errors) resolve. */
export function sessionIdForIndex(
  resolved: ResolvedQuery,
  index: number,
): string {
  return shortId(hashString(`${resolved.seed}#id${index}`));
}

function pickRepo(repos: string[], rng: Rng): string {
  const weights = repos.map((r) => repoWeight(r));
  const total = weights.reduce((a, b) => a + b, 0);
  let roll = rng.float() * total;
  for (let i = 0; i < repos.length; i += 1) {
    roll -= weights[i];
    if (roll <= 0) return repos[i];
  }
  return repos[repos.length - 1];
}

function sessionCost(
  modelMix: Array<{ name: string; calls: number }>,
  rng: Rng,
): CostBreakdown {
  const total = modelMix.reduce((a, m) => {
    const def = MODELS.find((d) => d.name === m.name);
    return a + m.calls * (def?.costPerCall ?? 0.03);
  }, 0);
  return buildCostDecomposition(total * rng.jitter(0.12), rng);
}

function buildOneSession(resolved: ResolvedQuery, index: number): SessionRow {
  const rng = new Rng(hashString(`${resolved.seed}#s${index}`));
  const session_id = sessionIdForIndex(resolved, index);
  const repo = pickRepo(resolved.repos, rng);

  // Long-tailed call counts: most sessions are short, a few are marathons.
  const callCount = Math.max(
    2,
    Math.round(6 + Math.pow(rng.float(), 3.2) * 420),
  );

  // Model mix: opus-led, occasionally joined by haiku (routing) or sonnet.
  const selected = resolved.models.length
    ? MODELS.filter((m) => resolved.models.includes(m.name))
    : MODELS;
  const primary = selected[0] ?? MODELS[0];
  const mix: Array<{ name: string; calls: number }> = [];
  let remaining = callCount;
  if (selected.length > 1 && rng.float() < 0.55) {
    const secondary = rng.pick(selected.slice(1));
    const secCalls = Math.round(callCount * rng.range(0.05, 0.3));
    mix.push({ name: secondary.name, calls: secCalls });
    remaining -= secCalls;
  }

  mix.unshift({ name: primary.name, calls: remaining });

  const cost = sessionCost(mix, rng);
  const tokensByClass = tokensFromCost(cost);
  const startMs =
    resolved.fromMs +
    rng.float() * Math.max(1, resolved.toMs - resolved.fromMs);
  const durationMs = (5 + Math.pow(rng.float(), 2) * 180) * 60_000;
  const endMs = Math.min(resolved.toMs, startMs + durationMs);

  const routedCalls =
    resolved.routed === false
      ? 0
      : (mix.find((m) => MODELS.find((d) => d.name === m.name)?.routeTarget)
          ?.calls ?? (rng.float() < 0.3 ? rng.int(0, 8) : 0));

  return {
    session_id,
    repo,
    experiment: resolved.experiments.length
      ? rng.pick(resolved.experiments)
      : rng.float() < 0.25
        ? rng.pick(EXPERIMENTS)
        : null,
    config_id: resolved.configs.length
      ? rng.pick(resolved.configs)
      : rng.pick(CONFIG_IDS),
    models: mix,
    call_count: callCount,
    started_at: new Date(startMs).toISOString(),
    ended_at: new Date(endMs).toISOString(),
    cost,
    tokens: {
      input: tokensByClass.input,
      output: tokensByClass.output,
      cache_read: tokensByClass.cache_read,
      cache_write: tokensByClass.cache_write,
    },
    routing: { routed_calls: routedCalls },
    cache_invalidations: rng.float() < 0.4 ? rng.int(1, 5) : 0,
  };
}

export interface SessionListResult {
  items: SessionRow[];
  total: number;
  next_cursor: string | null;
}

export function buildSessionList(
  resolved: ResolvedQuery,
  opts: { limit: number; cursor?: string; sort: SortKey; order: SortOrder },
  total: number,
): SessionListResult {
  const materialized = Math.min(total, MAX_MATERIALIZED);
  const rows: SessionRow[] = [];
  for (let i = 0; i < materialized; i += 1) {
    rows.push(buildOneSession(resolved, i));
  }

  const dir = opts.order === "asc" ? 1 : -1;
  rows.sort((a, b) => {
    let cmp: number;
    if (opts.sort === "started_at") {
      cmp = a.started_at.localeCompare(b.started_at);
    } else if (opts.sort === "call_count") {
      cmp = a.call_count - b.call_count;
    } else {
      cmp = a.cost.total - b.cost.total;
    }

    return cmp * dir;
  });

  const offset = opts.cursor ? Number(opts.cursor.replace("offset:", "")) : 0;
  const items = rows.slice(offset, offset + opts.limit);
  const nextOffset = offset + opts.limit;
  const next_cursor = nextOffset < rows.length ? `offset:${nextOffset}` : null;
  return { items, total, next_cursor };
}

/** Find or synthesize a session by id. The list is the source of truth; if an
 *  id isn't in the materialized window we synthesize a plausible detail from
 *  the id hash (so deep links never 404 in the mock). */
export function buildSessionDetail(
  resolved: ResolvedQuery,
  sessionId: string,
  total: number,
): SessionDetail | null {
  const materialized = Math.min(total, MAX_MATERIALIZED);
  let row: SessionRow | undefined;
  for (let i = 0; i < materialized; i += 1) {
    const r = buildOneSession(resolved, i);
    if (r.session_id === sessionId) {
      row = r;
      break;
    }
  }
  if (!row) {
    const synth = buildOneSession(resolved, hashString(sessionId) % 997);
    row = { ...synth, session_id: sessionId };
  }

  const rng = new Rng(hashString(`${sessionId}#detail`));
  const bytesBefore = Math.round(
    row.call_count * BASE.bytesBeforePerCall * rng.jitter(0.15),
  );
  const bias = biasAverages(row.repo ? [row.repo] : resolved.repos);
  // Context is the input side only; output is its own cost line (contract §4).
  const inputBill = row.cost.input + row.cost.cache_read + row.cost.cache_write;
  const ctx = buildContextBundle(
    inputBill,
    bytesBefore,
    1,
    bias,
    row.call_count,
    rng,
  );

  return {
    ...row,
    latency_ms: buildLatency(rng),
    transformers: ctx.transformers,
    context: {
      block_types: ctx.blocks,
      tools: ctx.tools,
      bash_commands: ctx.bash,
      prefix: ctx.prefix,
    },
  };
}

// ---- trends -----------------------------------------------------------------
/** Weekday/weekend rhythm: lighter on weekends, a midweek peak. */
function dayWeight(date: Date): number {
  const dow = date.getUTCDay(); // 0 = Sun
  const profile = [0.55, 1.05, 1.12, 1.15, 1.08, 0.95, 0.5];
  return profile[dow];
}

/** Intraday rhythm: a working-hours hump (UTC-centric but plausible). */
function hourWeight(date: Date): number {
  const h = date.getUTCHours();
  return 0.35 + 0.9 * Math.exp(-((h - 14) ** 2) / 40);
}

export function buildTrends(
  resolved: ResolvedQuery,
  bucket: TrendBucket,
  totals: { callCount: number; cost: CostBreakdown },
): TrendPoint[] {
  const step = bucket === "hour" ? HOUR_MS : DAY_MS;
  const start = Math.floor(resolved.fromMs / step) * step;
  const buckets: number[] = [];
  for (let t = start; t < resolved.toMs; t += step) buckets.push(t);
  if (!buckets.length) buckets.push(start);

  const rng = new Rng(`${resolved.seed}#trend-${bucket}`);
  const weights = buckets.map((t) => {
    const d = new Date(t);
    const w = bucket === "hour" ? dayWeight(d) * hourWeight(d) : dayWeight(d);
    return w * rng.jitter(0.18);
  });

  const calls = splitByWeights(totals.callCount, weights, true);
  const costTotals = splitByWeights(totals.cost.total, weights);

  return buckets.map((t, i) => {
    const pointCost = scaleCost(totals.cost, costTotals[i]);
    const tokensByClass = tokensFromCost(pointCost);
    return {
      bucket: new Date(t).toISOString(),
      call_count: calls[i],
      cost: pointCost,
      tokens: {
        input: tokensByClass.input,
        output: tokensByClass.output,
        cache_read: tokensByClass.cache_read,
        cache_write: tokensByClass.cache_write,
      },
    };
  });
}

/** Scale a cost decomposition to a new total, preserving the class mix. */
export function scaleCost(
  cost: CostBreakdown,
  newTotal: number,
): CostBreakdown {
  const f = cost.total > 0 ? newTotal / cost.total : 0;
  return {
    input: Math.round(cost.input * f * 10000) / 10000,
    cache_read: round2(cost.cache_read * f),
    cache_write: round2(cost.cache_write * f),
    output: round2(cost.output * f),
    total: round2(newTotal),
  };
}
